import { hasErrors, useNarrativeForm } from '../../hooks/useNarrativeForm';
import { validateNarrativeForm } from '../../utils';
import { useNarrativeErrorToast } from '../../hooks/useNarrativeErrorToast';
import { useNarrativeTracking } from '../../hooks/useNarrativeTracking';
import type { NarrativeForm } from '../../utils/schema';
import { useDebouncedCallback } from 'use-debounce';
import { transformFormValuesToValuation } from '../../utils/transformation';
import { useEffect, useRef } from 'react';
import type {
  FuturePEMultipleCalculationOutput,
  FuturePEMultipleFairValueCalculationOutput,
  FuturePEMultipleFairValueCalculationInput,
  FuturePEMultipleCalculationInput,
} from '../../../Narrative/components/Calculators/useFuturePEMultipleCalculation';
import { useNarrativeUpdates } from '../../hooks/useNarrativeUpdates';
import type { StartNarrativeInput } from '../../hooks/useStartNarrative/useStartNarrative';
import { useStartNarrativeWithSnackbar } from '../../hooks/useStartNarrative/useStartNarrative';
import { useNarrativeFormHandler } from '../../components/NarrativeFormContent';
import { validateDraftNarrativeForm } from '../../utils/validation';

type Props = {
  initialNarrativeForm: NarrativeForm;
  calculateFairValue: (
    values: FuturePEMultipleCalculationInput
  ) => FuturePEMultipleCalculationOutput;
  calculateRevenueGrowthRateFromFairValue: (
    values: FuturePEMultipleFairValueCalculationInput
  ) => FuturePEMultipleFairValueCalculationOutput;
  trackEvent: (data: Record<string, string | number | boolean>) => void;
  narrativeUpdateId?: string | null;
  narrativeId: string;
  companyId: string;
  successSnackbar: boolean;
  setNarrativeUpdateId?: (narrativeUpdateId: string) => void;
  setNarrativeId?: (narrativeId: string) => void;
  disableAutoSave?: boolean;
};

const CONTENT_VALUATION_DEBOUNCE_TIME = 1000;
const START_NARRATIVE_DEBOUNCE_TIME = 1000;

export const useNarrativeFormManager = ({
  initialNarrativeForm,
  trackEvent,
  narrativeUpdateId,
  narrativeId,
  successSnackbar,
  setNarrativeUpdateId,
  setNarrativeId,
  calculateFairValue,
  calculateRevenueGrowthRateFromFairValue,
  companyId,
  disableAutoSave,
}: Props) => {
  const hasStartedNarrative = useRef(narrativeId ? true : false);

  const {
    values,
    errors,
    setSubmitCount,
    submitCount,
    validateForm,
    dirty,
    isDirty,
    resetForm,
    setPartialValues,
  } = useNarrativeForm({
    initialValues: initialNarrativeForm,
    validate: validateNarrativeForm,
    enableReinitialize: true,
  });

  useNarrativeErrorToast({ errors, submitCount });
  useNarrativeTracking({
    trackEvent: trackEvent,
    dirty,
    errors,
  });

  const {
    handleSetPartialValues,
    handleSetFairValue,
    handleContentChange,
    handleTitleChange,
  } = useNarrativeFormHandler({
    calculateFairValue,
    calculateRevenueGrowthRateFromFairValue,
    setPartialValues,
    values,
  });

  const { handleUpdateNarrative, updateNarrativeUpdateStatus } =
    useNarrativeUpdates({
      includeSuccessSnackbar: successSnackbar,
    });

  const { mutate: startNarrativeAsync, status: startNarrativeStatus } =
    useStartNarrativeWithSnackbar({
      includeSuccessSnackbar: successSnackbar,
    });

  const debouncedHandleUpdateNarrative = useDebouncedCallback(
    handleUpdateNarrative,
    CONTENT_VALUATION_DEBOUNCE_TIME
  );

  const startNarrativeWithHandleResponse = async (
    input: StartNarrativeInput
  ) => {
    hasStartedNarrative.current = true;
    const response = await startNarrativeAsync(input);

    setNarrativeId?.(response.startNarrative.id ?? '');
    setNarrativeUpdateId?.(response.startNarrative.draftUpdate?.id ?? '');
  };

  const debouncedStartNarrative = useDebouncedCallback(
    startNarrativeWithHandleResponse,
    START_NARRATIVE_DEBOUNCE_TIME
  );

  // Should handle initial narrative creation if none exists
  useEffect(() => {
    if (isDirty && !hasStartedNarrative.current && !narrativeId) {
      if (hasErrors(validateDraftNarrativeForm(values))) {
        return;
      }

      debouncedStartNarrative({
        input: {
          companyId: companyId,
          coreHtml: values.content.html,
          title: values.title,
          valuation: transformFormValuesToValuation({ values }),
          userHoldsPosition: values.userHoldsPosition ?? false,
          setAsPrimary: true,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, values]);

  // Should handle once when the narrative is created
  useEffect(() => {
    if (disableAutoSave) {
      return;
    }

    if (narrativeId) {
      hasStartedNarrative.current = true;
    }

    if (isDirty && narrativeId && hasStartedNarrative.current) {
      if (hasErrors(validateDraftNarrativeForm(values))) {
        return;
      }

      debouncedHandleUpdateNarrative({
        coreHtml: values.content.html,
        title: values.title,
        valuation: transformFormValuesToValuation({ values }),
        rebase: false,
        userHoldsPosition: values.userHoldsPosition ?? false,
        narrativeUpdateId: narrativeUpdateId ?? '',
      });
    }
  }, [
    isDirty,
    values,
    debouncedHandleUpdateNarrative,
    validateForm,
    narrativeId,
    disableAutoSave,
    narrativeUpdateId,
  ]);

  return {
    values,
    errors,
    setSubmitCount,
    submitCount,
    validateForm,
    dirty,
    resetForm,
    handleSetPartialValues,
    handleSetFairValue,
    handleContentChange,
    handleTitleChange,
    updateNarrativeUpdateStatus,
    startNarrativeStatus,
  };
};
